import { useState } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Building2, Globe, Award } from "lucide-react";
import { GalleryLightbox } from "@/components/GalleryLightbox";

const Affiliations = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const certificateImages = [
    {
      src: "/lovable-uploads/0af8d272-30c8-4e4c-8ded-3cf6f382812e.png",
      alt: "Pioneer Industrial Strategic Partnership Letter",
      caption: "Strategic Partnership Acknowledgment with Pioneer Industrial Pte Ltd"
    },
    {
      src: "/lovable-uploads/56d97760-1ecd-48f9-99f5-6d5a646977db.png",
      alt: "Cobetter Authorized Distributor Certificate",
      caption: "Official Authorized Distributor Certificate for Cobetter Filtration"
    }
  ];

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  return (
    <>
      <Helmet>
        <title>Strategic Affiliations | WLE Technology - Industrial Partnerships</title>
        <meta name="description" content="WLE Technology partners with Pioneer Industrial and Cobetter Filtration to deliver world-class industrial filtration, stainless tank fabrication, and technical solutions across Asia-Pacific." />
        <meta name="keywords" content="WLE Technology affiliations, strategic partnerships, industrial filtration, stainless tank fabrication, Asia-Pacific distributor, Pioneer Industrial Singapore, Cobetter Filtration Malaysia, authorized distributor, industrial equipment, tank fabrication partners" />
        <link rel="canonical" href="https://wletec.com/affiliations" />
        <meta property="og:title" content="Strategic Affiliations | WLE Technology - Industrial Partnerships" />
        <meta property="og:description" content="WLE Technology partners with Pioneer Industrial and Cobetter Filtration to deliver world-class industrial filtration, stainless tank fabrication, and technical solutions across Asia-Pacific." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://wletec.com/affiliations" />
        <meta property="og:image" content="https://wletec.com/__l5e/assets-v1/31da0235-8220-40f8-b3f7-788933313494/official-wle-logo.webp" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Strategic Affiliations | WLE Technology - Industrial Partnerships" />
        <meta property="twitter:description" content="WLE Technology partners with Pioneer Industrial and Cobetter Filtration to deliver world-class industrial filtration, stainless tank fabrication, and technical solutions across Asia-Pacific." />
        <meta property="twitter:image" content="https://wletec.com/__l5e/assets-v1/31da0235-8220-40f8-b3f7-788933313494/official-wle-logo.webp" />
      </Helmet>
      
      <div className="min-h-screen bg-page-modern">
        <Navigation />
      
        <main className="py-20">
          <div className="container mx-auto px-4">
            {/* Header */}
            <div className="text-center mb-16 animate-fade-in">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Strategic Partners</h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                WLE Technology is proud to partner with industry leaders to deliver world-class solutions across Asia-Pacific
              </p>
            </div>

            {/* Partners Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-16 animate-fade-in">
            {/* Pioneer Industrial */}
            <Card className="overflow-hidden">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <Building2 className="h-6 w-6 text-primary" />
                    <span>Pioneer Industrial Pte Ltd</span>
                  </CardTitle>
                  <Badge variant="secondary">Singapore</Badge>
                </div>
                <CardDescription>Industrial Products & Services Provider</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Established since 2016, Pioneer Industrial Pte Ltd is a Singapore-based company providing 
                    industrial products and services across Asia. They specialize in authorized distribution 
                    of prestigious brands and offer independent repair & accredited testing services.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <Award className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-sm">Expertise</h4>
                        <p className="text-xs text-muted-foreground">Authorized distributor for industrial valves and equipment with workshop facilities</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-2">
                      <Globe className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-sm">Coverage</h4>
                        <p className="text-xs text-muted-foreground">Strong network across Asia-Pacific with sales and technical support</p>
                      </div>
                    </div>
                  </div>

                  {/* Partnership Letter */}
                  <div className="pt-6 border-t space-y-4">
                    <div className="bg-muted/30 rounded-lg p-6 hover:bg-muted/40 transition-colors">
                      <h4 className="font-medium text-sm mb-3">Strategic Partnership Acknowledgment</h4>
                      <div 
                        className="cursor-pointer group"
                        onClick={() => openLightbox(0)}
                      >
                        <img 
                          src="/lovable-uploads/0af8d272-30c8-4e4c-8ded-3cf6f382812e.png" 
                          alt="Pioneer Industrial Strategic Partnership Letter" 
                          className="w-full h-auto rounded border shadow-sm group-hover:shadow-md transition-shadow"
                          loading="lazy"
                          decoding="async"
                          width="400"
                          height="300"
                        />
                        <p className="text-xs text-muted-foreground mt-2 text-center group-hover:text-foreground transition-colors">
                          Click to view full size
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <a 
                      href="https://www.pioneerindustrial.com.sg/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
                    >
                      <span className="text-sm">Visit Website</span>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cobetter */}
            <Card className="overflow-hidden">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <img 
                      src="/lovable-uploads/d976127c-d7e5-473a-977f-96357e3e59f3.png" 
                      alt="Cobetter Filtration Logo" 
                      className="h-12 w-auto"
                    />
                    <div>
                      <CardTitle className="text-xl">Cobetter Filtration</CardTitle>
                      <Badge variant="outline" className="text-xs">Authorized Distributor</Badge>
                    </div>
                  </div>
                  <Badge variant="secondary">China</Badge>
                </div>
                <CardDescription>Filtration, Separation and Purification Solutions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    WLE Technology is the authorized distributor for Cobetter Filtration products in Malaysia. 
                    Cobetter is a leading global provider of filtration and purification solutions for 
                    Food & Beverage and Pharmaceutical industries.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <Award className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-sm">Authorization</h4>
                        <p className="text-xs text-muted-foreground">Official distributor for Food & Beverage and Pharmaceutical industries</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-2">
                      <Globe className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-sm">Products</h4>
                        <p className="text-xs text-muted-foreground">Membrane filters, TFF cassettes, sterile filtration systems</p>
                      </div>
                    </div>
                  </div>

                  {/* Certificate Image */}
                  <div className="pt-6 border-t space-y-4">
                    <div className="bg-muted/30 rounded-lg p-6 hover:bg-muted/40 transition-colors">
                      <h4 className="font-medium text-sm mb-3">Authorized Distributor Certificate</h4>
                      <div 
                        className="cursor-pointer group"
                        onClick={() => openLightbox(1)}
                      >
                        <img 
                          src="/lovable-uploads/56d97760-1ecd-48f9-99f5-6d5a646977db.png" 
                          alt="Cobetter Authorized Distributor Certificate" 
                          className="w-full h-auto rounded border shadow-sm group-hover:shadow-md transition-shadow"
                          loading="lazy"
                          decoding="async"
                          width="400"
                          height="300"
                        />
                        <p className="text-xs text-muted-foreground mt-2 text-center group-hover:text-foreground transition-colors">
                          Click to view full size
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <a 
                      href="https://cobetter.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
                    >
                      <span className="text-sm">Visit Website</span>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
            </div>

            {/* Partnership Benefits */}
            <div className="bg-muted/50 rounded-lg p-8 md:p-12 animate-fade-in">
              <h2 className="text-2xl font-bold mb-8 text-center">Partnership Benefits</h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center hover-scale">
                  <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4 hover:bg-primary/20 transition-colors">
                    <Globe className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-3 text-lg">Global Reach</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Access to international markets and distribution networks across Asia-Pacific
                  </p>
                </div>
                
                <div className="text-center hover-scale">
                  <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4 hover:bg-primary/20 transition-colors">
                    <Award className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-3 text-lg">Quality Assurance</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Certified products meeting international standards and regulations
                  </p>
                </div>
                
                <div className="text-center hover-scale">
                  <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4 hover:bg-primary/20 transition-colors">
                    <Building2 className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-3 text-lg">Technical Support</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Comprehensive technical expertise and dedicated support services
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
        
        <GalleryLightbox
          items={certificateImages}
          startIndex={lightboxIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
        />
      </div>
    </>
  );
};

export default Affiliations;